import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {selectCalendarWidgetState} from '../../store/reducers/calendar';
import {AppointmentModel} from '../models/calendar/appointments.models';
import {takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import moment from 'moment';
import {ISO_FROM_BACKEND} from '../../core/constants/date-and-time-formats.constants';
import {AppointmentType} from '../constants/appointmentTypes.constants';
import {ViewingAvailabilityModel} from '../models/properties/property-availability.models';
import { CalendarWidgetActions } from '../../store/actions/calendar/calendar-widget.actions';

@Injectable({
  providedIn: 'root'
})
export class CalendarWidgetService {

  private allowedAppointmentTypes = [AppointmentType.VIEWINGS, AppointmentType.VALUATIONS, AppointmentType.STAFF_APPOINTMENTS];

  constructor(private store: Store) {
  }

  /**
   * Assigns allowed appointment types to correct time slot
   * @param appointments: AppointmentModel[]
   */
  assignAppointmentsToCorrectTimeSlots(appointments: AppointmentModel[]): void {
    const destroy$ = new Subject<boolean>();
    this.store.select(selectCalendarWidgetState)
      .pipe(takeUntil(destroy$))
      .subscribe((state) => {
        if (state.agentAppointments === null) { return; }

        const availability = JSON.parse(JSON.stringify(state.availability));
        const filteredAppointments = this.filterOutNeededAppointments(appointments);

        if (!Array.isArray(availability)) {
          return;
        }

        const currentAgentId = state.hostId;
        availability.forEach((timeSlotGroup, groupIndex) => {
          if (!Array.isArray(timeSlotGroup)) {
            return;
          }

          filteredAppointments.forEach((appointment) => {
            const appointmentStart = moment(appointment.appointmentStart, ISO_FROM_BACKEND);
            const appointmentEnd = moment(appointment.appointmentEnd, ISO_FROM_BACKEND);

            timeSlotGroup.forEach((timeSlotEntry, entryIndex) => {
              const timeSlotStart = moment(timeSlotEntry.startDateTime, ISO_FROM_BACKEND);

              if (timeSlotStart.isSame(appointmentStart) || timeSlotStart.isBetween(appointmentStart, appointmentEnd)) {
                availability[groupIndex][entryIndex].appointment = appointment;
              }
            });
          });
        });

        this.dispatchAvailability(availability);

        destroy$.next(true);
        destroy$.complete();
      });
  }

  /**
   * Dispatch new availability
   * @param availability: Array<ViewingAvailabilityModel[]>
   */
  private dispatchAvailability(availability: Array<ViewingAvailabilityModel[]>): void {
    this.store.dispatch(CalendarWidgetActions.setPropertyViewingAvailability({
      data: availability
    }));
  }

  /**
   * Filters out only needed appointments
   * @param appointments: AppointmentModel[]
   */
  private filterOutNeededAppointments(appointments: AppointmentModel[]): AppointmentModel[] {
      return appointments.filter((appointment) =>
        !appointment.relatedType || this.allowedAppointmentTypes.includes(appointment.relatedType)
      );
  }

}
